import { X, Plus, ChevronLeft, ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { useWorkspace, type WorkspaceTab } from "./workspace-context";

interface WorkspaceTabBarProps {
  className?: string;
}

function getTabIcon(tab: WorkspaceTab) {
  if (tab.route === "/workspaces/new") return "+";
  if (tab.route.startsWith("/specPilot/")) return "📄";
  if (tab.route.startsWith("/revPilot/")) return "🔄";
  if (tab.route.startsWith("/revCollab/revisions/")) return "🗂️";
  if (tab.route.startsWith("/revCollab/documents/")) return "🗂️";
  if (tab.route === "/revCollab") return "🗂️";
  if (tab.route === "/inbox") return "📥";
  if (tab.route === "/" || tab.route === "/projects") return "🏠";
  if (tab.route === "/specPilot/new") return "📄";
  if (tab.route === "/revPilot") return "🔄";
  if (tab.route === "/revPilot/new") return "🔄";
  return "📄";
}

export function WorkspaceTabBar({ className }: WorkspaceTabBarProps) {
  const { tabs, activeTabId, setActiveTab, closeTab, openTab, goBackInActiveTab, goForwardInActiveTab } = useWorkspace();

  const activeTab = tabs.find((t) => t.id === activeTabId);
  const canGoBack = activeTab ? activeTab.historyIndex > 0 : false;
  const canGoForward = activeTab ? activeTab.historyIndex < activeTab.history.length - 1 : false;

  return (
    <div
      className={cn(
        "flex items-center gap-1 border-b border-border bg-muted/50 px-2 py-1.5 overflow-x-auto",
        className
      )}
    >
      {/* Back / Forward */}
      <Button
        variant="ghost"
        size="icon"
        className="size-7 shrink-0"
        disabled={!canGoBack}
        onClick={() => goBackInActiveTab()}
        title="Go back"
      >
        <ChevronLeft className="size-4" />
      </Button>
      <Button
        variant="ghost"
        size="icon"
        className="size-7 shrink-0"
        disabled={!canGoForward}
        onClick={() => goForwardInActiveTab()}
        title="Go forward"
      >
        <ChevronRight className="size-4" />
      </Button>

      <div className="mx-1 h-4 w-px bg-border shrink-0" />

      {/* Tabs */}
      {tabs.map((tab) => {
        const isActive = activeTabId === tab.id;

        return (
          <div
            key={tab.id}
            className={cn(
              "group flex items-center gap-0.5 rounded-md min-w-0",
              isActive
                ? "bg-background shadow-sm border border-border"
                : "hover:bg-background/60"
            )}
          >
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setActiveTab(tab.id)}
              className={cn(
                "h-auto min-w-0 flex-1 justify-start gap-1.5 rounded-md px-2.5 py-1.5 text-left text-sm font-medium transition-all select-none",
                isActive
                  ? "text-foreground"
                  : "text-muted-foreground hover:text-foreground"
              )}
            >
              <span className="shrink-0">{getTabIcon(tab)}</span>
              <span className="truncate max-w-[150px]">{tab.title}</span>
            </Button>
            {tabs.length > 1 && (
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="size-5 opacity-0 group-hover:opacity-100 hover:bg-muted shrink-0 rounded-md"
                onClick={(e) => {
                  e.stopPropagation();
                  closeTab(tab.id);
                }}
                aria-label="Close tab"
              >
                <X className="size-3" />
              </Button>
            )}
          </div>
        );
      })}

      {/* New Tab Button */}
      <Button
        variant="ghost"
        size="icon"
        className="size-8 shrink-0"
        onClick={() => openTab()}
        title="New Tab"
      >
        <Plus className="size-4" />
      </Button>
    </div>
  );
}
